import { type ReactNode } from 'react';

import { RiAddLine, RiArrowDownSLine } from '@remixicon/react';

import { Button } from '@spotify-internal/encore-web';
import {
  ActionMenu,
  MenuOption,
  useDialog,
} from '@spotify/confidence-core-react';
import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';

import { type WorkflowExtensionOptions } from '../../extensions';
import { getWorkflowName } from '../../helpers';

const CreateExperimentMenuItem = ({
  ext,
  onClick: onClose,
}: {
  ext: WorkflowExtensionOptions;
  onClick?: () => void;
}) => {
  const { openDialog } = useDialog();
  const { allowed } = useCheckResourcePermissions({
    can: 'create_workflow_instance',
    name: getWorkflowName(ext.workflowId),
    optimistic: true,
  });

  const WorkflowIcon = ext.workflowIcon;

  return (
    <MenuOption
      key={ext.workflowId}
      label={ext.title}
      icon={<WorkflowIcon />}
      disabled={!allowed}
      onClick={() => {
        onClose?.();
        const content = ext.createComponent?.();
        if (content) openDialog({ content });
      }}
    />
  );
};

type CreateExperimentMenuProps = {
  extensions: WorkflowExtensionOptions[];
};

export const CreateExperimentMenu = ({
  extensions,
}: CreateExperimentMenuProps): ReactNode => (
  <ActionMenu
    id="create-experiment-menu"
    buttons={extensions.map(ext => (
      <CreateExperimentMenuItem key={ext.workflowId} ext={ext} />
    ))}
    renderButton={({ endIcon: _, ...props }, open) => (
      <Button
        {...props}
        variant="primary"
        size="small"
        aria-expanded={open ? true : undefined}
      >
        <RiAddLine size={16} />
        Create
        <RiArrowDownSLine size={16} />
      </Button>
    )}
  />
);
